import config from 'config';
import { deleteCookie, getCookie, responseError, responseErrorWithMessage } from 'helpers';
import { InsurerConsultorios, APIResponse } from 'types/Request';
import {
  Consultorio,
  ConsultorioInfoWithStaff,
  ConsultorioCokiba,
  ConsultorioCokibaBD,
  HorariosAtencion,
  ConsultorioBaja,
  ConsultorioRenovacion,
  ConsultorioUpdateEdilicia,
  ConsultorioUpdateDirector,
  ConsultorioUpdatePropietario,
} from 'types/Consultorio';

interface GetConsultoriosOptions {
  insurerId: number;
  matriculateId: number;
  date?: string; // 2022-07-18
}

interface GetConsultoriosUPOptions {
  matriculateId: number;
}

interface GetConsultoriosOmintOptions {
  matriculateId: number;
}

function revokeCredentials() {
  deleteCookie('accessToken');
  deleteCookie('sessionCookie');
  // eslint-disable-next-line no-restricted-globals
  return location.reload();
}

export async function getConsultorios(options: GetConsultoriosOptions): Promise<InsurerConsultorios | undefined> {
  try {
    const { insurerId, matriculateId, date } = options;

    const url = new URL(`${ config.baseUrl }/insurers/${ insurerId }/consultorios`);

    const params = new URLSearchParams({
      matriculate_id: matriculateId.toString(),
    });

    if (date) {
      params.append('date', date);
    }

    url.search = params.toString();

    type Response = APIResponse<InsurerConsultorios>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
  }
}

export async function getConsultoriosUP(options: GetConsultoriosUPOptions): Promise<InsurerConsultorios | undefined> {
  try {
    const { matriculateId } = options;

    const url = new URL(`${ config.baseUrl }/insurers/consultorios-up`);

    const params = new URLSearchParams({
      matriculate_id: matriculateId.toString(),
    });

    url.search = params.toString();

    type Response = APIResponse<InsurerConsultorios>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
  }
}

export async function getConsultorioInfoWithStaff(
  consultorio: number,
): Promise<ConsultorioInfoWithStaff | undefined> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/${ consultorio }`);

    type Response = APIResponse<ConsultorioInfoWithStaff>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
        'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (1)');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
      'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (2)');
  }
}

export async function getConsultoriosOmint(
  options: GetConsultoriosOmintOptions,
): Promise<InsurerConsultorios | undefined> {
  try {
    const { matriculateId } = options;

    const url = new URL(`${ config.baseUrl }/insurers/consultorios-omint`);

    const params = new URLSearchParams({
      matriculate_id: matriculateId.toString(),
    });

    url.search = params.toString();

    type Response = APIResponse<InsurerConsultorios>
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
  }
}

export async function getAllConsultoriosCokiba(matricula: string | null = null): Promise<ConsultorioCokiba[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/cokiba`);

    type Response = APIResponse<ConsultorioCokiba[]>

    if (matricula) {
      const params = new URLSearchParams({
        matricula: matricula.toString(),
      });
      url.search = params.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
  }
}

export async function getAllConsultoriosAsDirector(
  matricula: string | null = null,
): Promise<ConsultorioCokibaBD[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/as-director`);

    type Response = APIResponse<ConsultorioCokibaBD[]>

    if (matricula) {
      const params = new URLSearchParams({
        matricula: matricula.toString(),
      });
      url.search = params.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
  }
}

export async function getAllSolicitudesConsultorios(matricula: string | null = null): Promise<Consultorio[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/solicitudes`);

    type Response = APIResponse<Consultorio[]>

    if (matricula) {
      const params = new URLSearchParams({
        matricula: matricula.toString(),
      });
      url.search = params.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
  }
}

export async function getConsultorioRequest(consultorioId: number): Promise<Consultorio | undefined> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/consultorios-request/${ consultorioId }`);

    type Response = APIResponse<Consultorio>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
  }
}

export async function postConsultorio(
  data: Partial<Consultorio>,
  files: Record<string, File>,
  totpCode: string,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
        'x-totp-code': totpCode,
      },
      body: formData,
    });

    if (res.status === 400) return responseErrorWithMessage(res);

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer POST de consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function putConsultorio(
  data: Partial<Consultorio>,
  files: Record<string, File>,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/${data.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer PUT de consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function putTelefonoConsultorio(
  data: Partial<ConsultorioInfoWithStaff>,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/${data.consultorio_id}/telefono`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer PUT de consultorios');
    console.info(data);
    console.info(err);
    throw err;
  }
}

export async function putHorariosConsultorio(
  id: number,
  data: HorariosAtencion[],
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/${id}/horarios`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer PUT de consultorios');
    console.info(data);
    console.info(err);
    throw err;
  }
}

export async function getAllSolicitudesConsultoriosBaja(
  matricula: string | null = null,
): Promise<ConsultorioBaja[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/baja/solicitudes`);

    type Response = APIResponse<ConsultorioBaja[]>

    if (matricula) {
      const params = new URLSearchParams({
        matricula: matricula.toString(),
      });
      url.search = params.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
  }
}

export async function getConsultorioBajaRequest(
  consultorioBajaId: number,
): Promise<ConsultorioBaja | undefined> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates/consultorios-baja-request/${ consultorioBajaId }`);

    type Response = APIResponse<ConsultorioBaja>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
  }
}

export async function postConsultorioBaja(
  data: Partial<ConsultorioBaja>,
  files: Record<string, File>,
  totpCode: string,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/baja`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
        'x-totp-code': totpCode,
      },
      body: formData,
    });

    if (res.status === 400) return responseErrorWithMessage(res);

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer POST de consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function putConsultorioBaja(
  data: Partial<ConsultorioBaja>,
  files: Record<string, File>,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/baja/${data.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer PUT de consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function getAllSolicitudesConsultoriosRenovacion(
  matricula: string | null = null,
): Promise<ConsultorioRenovacion[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/renovacion/solicitudes`);

    type Response = APIResponse<ConsultorioRenovacion[]>

    if (matricula) {
      const params = new URLSearchParams({
        matricula: matricula.toString(),
      });
      url.search = params.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
  }
}

export async function getConsultorioRenovacionRequest(
  consultorioRenovacionId: number,
): Promise<ConsultorioRenovacion | undefined> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates` +
      `/consultorios-renovacion-request/${ consultorioRenovacionId }`);

    type Response = APIResponse<ConsultorioRenovacion>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
  }
}

export async function postConsultorioRenovacion(
  data: Partial<ConsultorioRenovacion>,
  files: Record<string, File>,
  totpCode: string,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/renovacion`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
        'x-totp-code': totpCode,
      },
      body: formData,
    });

    if (res.status === 400) return responseErrorWithMessage(res);

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer POST de consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function putConsultorioRenovacion(
  data: Partial<ConsultorioRenovacion>,
  files: Record<string, File>,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/renovacion/${data.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer PUT de consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function getAllSolicitudesConsultoriosUpdateEdilicia(
  matricula: string | null = null,
): Promise<ConsultorioUpdateEdilicia[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/update/edilicia/solicitudes`);

    type Response = APIResponse<ConsultorioUpdateEdilicia[]>

    if (matricula) {
      const params = new URLSearchParams({
        matricula: matricula.toString(),
      });
      url.search = params.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
  }
}

export async function getConsultorioUpdateEdiliciaRequest(
  consultorioUpdateId: number,
): Promise<ConsultorioUpdateEdilicia | undefined> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates` +
      `/consultorios-update-edilicia-request/${ consultorioUpdateId }`);

    type Response = APIResponse<ConsultorioUpdateEdilicia>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
  }
}

export async function postConsultorioUpdateEdilicia(
  data: Partial<ConsultorioUpdateEdilicia>,
  files: Record<string, File>,
  totpCode: string,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/update/edilicia`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
        'x-totp-code': totpCode,
      },
      body: formData,
    });

    if (res.status === 400) return responseErrorWithMessage(res);

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer POST de consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function putConsultorioUpdateEdilicia(
  data: Partial<ConsultorioUpdateEdilicia>,
  files: Record<string, File>,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/update/edilicia/${data.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer PUT de consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function getAllSolicitudesConsultoriosUpdateDirector(
  matricula: string | null = null,
): Promise<ConsultorioUpdateDirector[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/update/director/solicitudes`);

    type Response = APIResponse<ConsultorioUpdateDirector[]>

    if (matricula) {
      const params = new URLSearchParams({
        matricula: matricula.toString(),
      });
      url.search = params.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
  }
}

export async function getConsultorioUpdateDirectorRequest(
  consultorioUpdateId: number,
): Promise<ConsultorioUpdateDirector | undefined> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates` +
      `/consultorios-update-director-request/${ consultorioUpdateId }`);

    type Response = APIResponse<ConsultorioUpdateDirector>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
  }
}

export async function postConsultorioUpdateDirector(
  data: Partial<ConsultorioUpdateDirector>,
  files: Record<string, File>,
  totpCode: string,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/update/director`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
        'x-totp-code': totpCode,
      },
      body: formData,
    });

    if (res.status === 400) return responseErrorWithMessage(res);

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer POST de consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function putConsultorioUpdateDirector(
  data: Partial<ConsultorioUpdateDirector>,
  files: Record<string, File>,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/update/director/${data.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer PUT de consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function getAllSolicitudesConsultoriosUpdatePropietario(
  matricula: string | null = null,
): Promise<ConsultorioUpdatePropietario[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/update/propietario/solicitudes`);

    type Response = APIResponse<ConsultorioUpdatePropietario[]>

    if (matricula) {
      const params = new URLSearchParams({
        matricula: matricula.toString(),
      });
      url.search = params.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones.');
  }
}

export async function getConsultorioUpdatePropietarioRequest(
  consultorioUpdateId: number,
): Promise<ConsultorioUpdatePropietario | undefined> {
  try {
    const url = new URL(`${ config.baseUrl }/matriculates` +
      `/consultorios-update-propietario-request/${ consultorioUpdateId }`);

    type Response = APIResponse<ConsultorioUpdatePropietario>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar la solicitud de consultorio.');
  }
}

export async function postConsultorioUpdatePropietario(
  data: Partial<ConsultorioUpdatePropietario>,
  files: Record<string, File>,
  totpCode: string,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/update/propietario`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
        'x-totp-code': totpCode,
      },
      body: formData,
    });

    if (res.status === 400) return responseErrorWithMessage(res);

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer POST de consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function putConsultorioUpdatePropietario(
  data: Partial<ConsultorioUpdatePropietario>,
  files: Record<string, File>,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/consultorios/update/propietario/${data.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer PUT de consultorios');
    console.info(data, files);
    console.info(err);
    throw err;
  }
}

export async function getAllConsultoriosAsStaff(
  matricula: string | null = null,
): Promise<ConsultorioCokibaBD[] | []> {
  try {
    const url = new URL(`${ config.baseUrl }/consultorios/as-staff`);

    type Response = APIResponse<ConsultorioCokibaBD[]>

    if (matricula) {
      const params = new URLSearchParams({
        matricula: matricula.toString(),
      });
      url.search = params.toString();
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return [];
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return [];
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
        'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (1)');
    }

    return content.payload ?? [];
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
      'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (2)');
  }
}
